import { WORK_AS_HEADER } from '@openx/constants/workAs.ts';
import type { WorkAsLocalStorageData } from '@openx/types/impersonation.ts';
import { WORK_AS_USER_SESSION_LOCALSTORAGE_KEY } from '../session/constants.ts';

/**
 * Applies the stored impersonation headers to an outgoing request.
 *
 * The persisted payload is not guaranteed to be complete — older app versions and test fixtures
 * write partial objects — and header *names* come straight from it. Setting them unchecked either
 * emits a literal `undefined` header or makes `Headers.set` throw on an empty name, which the
 * surrounding catch would swallow, silently dropping impersonation from the request. So each pair
 * is validated before use: without a usable work-as name and value no impersonation headers are
 * applied at all, and the ip pair is independent of it.
 */
export function applyWorkAsHeadersFromLocalStorage(headers: Headers): void {
  if (headers.has(WORK_AS_HEADER)) {
    return;
  }

  const workAsUserLocalStorageData = localStorage.getItem(WORK_AS_USER_SESSION_LOCALSTORAGE_KEY);
  if (!workAsUserLocalStorageData) {
    return;
  }

  try {
    const { workAsHeader, workAsValue, ipHeader, ipValue } = JSON.parse(
      workAsUserLocalStorageData
    ) as Partial<WorkAsLocalStorageData>;

    if (!workAsHeader || !workAsValue) {
      return;
    }

    headers.set(workAsHeader, workAsValue);

    if (ipHeader && ipValue) {
      headers.set(ipHeader, ipValue);
    }
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: matches axios work-as interceptor behavior
    console.error(error);
  }
}
